import CryptoJS from 'crypto-js';
import { StateStorage } from 'zustand/middleware';

const SECRETS_WATERMARK = 'SatuWajanSecure_ClientStorageKey_2026_';

/**
 * Derives a secure, stable client fingerprint/key.
 * Integrates userAgent variables, but has a robust fallback to the primary watermark if decryption fails.
 */
const getSecretKey = (): string => {
  if (typeof window === 'undefined') return SECRETS_WATERMARK;
  const hardwareFingerprint = window.navigator.userAgent || '';
  return `${SECRETS_WATERMARK}_${hardwareFingerprint}`;
};

/**
 * Encrypts a plaintext string using AES-256
 */
export const encryptData = (plainText: string): string => {
  try {
    const key = getSecretKey();
    return CryptoJS.AES.encrypt(plainText, key).toString();
  } catch (error) {
    console.error('[Encryption Error]', error);
    return plainText;
  }
};

/**
 * Decrypts an AES-encrypted string, attempting direct environment fallback to prevent data loss
 */
export const decryptData = (cipherText: string): string => {
  try {
    if (!cipherText) return '';
    
    // Check if the input is valid JSON first (e.g. if we are migrating from unencrypted state)
    if (cipherText.trim().startsWith('{') || cipherText.trim().startsWith('[')) {
      return cipherText;
    }
    
    const key = getSecretKey();
    const bytes = CryptoJS.AES.decrypt(cipherText, key);
    const decrypted = bytes.toString(CryptoJS.enc.Utf8);
    
    if (!decrypted) {
      // Fallback decryption with base watermark (e.g., if the userAgent changed)
      const bytesFallback = CryptoJS.AES.decrypt(cipherText, SECRETS_WATERMARK);
      return bytesFallback.toString(CryptoJS.enc.Utf8);
    }
    
    return decrypted;
  } catch (error) {
    // If decryption error occurs, check if it's already plain/unencrypted to prevent system lockouts
    if (cipherText.trim().startsWith('{') || cipherText.trim().startsWith('[')) {
      return cipherText;
    }
    console.error('[Decryption Error]', error);
    return '';
  }
};

/**
 * Custom secure Zustand StateStorage implementation that automatically encrypts and decrypts items
 */
export const secureStorage: StateStorage = {
  getItem: (name: string): string | null => {
    try {
      const rawValue = localStorage.getItem(name);
      if (!rawValue) return null;
      const decrypted = decryptData(rawValue);
      return decrypted || null;
    } catch (e) {
      return null;
    }
  },
  setItem: (name: string, value: string): void => {
    try {
      const encrypted = encryptData(value);
      localStorage.setItem(name, encrypted);
    } catch (e) {
      // ignore
    }
  },
  removeItem: (name: string): void => {
    try {
      localStorage.removeItem(name);
    } catch (e) {}
  },
};
